// v7.3.x client-resilience helpers, extracted for testability.
//
// These back three end-user fixes surfaced by the KYC upload flow on mobile:
//   • Fix #2: KYC wizard step + dashboard section survive a mobile page reload
//             (opening the camera/gallery frequently discards the page). We use
//             sessionStorage (tab-scoped) so it survives reloads but clears when
//             the tab closes — it never "sticks" across separate visits.
//   • Fix #3: the "Take Photo" button is never silently dead — if the in-app
//             camera is unavailable/blocked we fall back to the native picker AND
//             surface a clear message.

export const CAMERA_UNAVAILABLE_MESSAGE =
  "📷 The in-app camera isn't available in this browser. Opening your device camera/photo picker instead — pick a photo, or tap 'Choose File'.";
export const CAMERA_BLOCKED_MESSAGE =
  "📷 We couldn't open the in-app camera (it may be blocked or unsupported here). Opening your device camera/photo picker instead — if nothing happens, tap 'Choose File' and select a photo.";
// Appended to the fallback messages when the page is running inside an in-app
// browser (e.g. the Google app, Facebook, Instagram, TikTok WebView). These
// WebViews frequently block camera access AND the native picker, so the only
// reliable fix is to reopen the page in a real browser.
export const IN_APP_BROWSER_HINT =
  " ⚠️ You appear to be using an in-app browser (like the Google, Facebook or Instagram app). Camera and file uploads are often blocked here — please open aureus.africa in Chrome or Safari to complete your KYC.";

const KYC_STEP_KEY = (userId: number | string) => `aureus_kyc_step_${userId}`;
const ACTIVE_SECTION_KEY = 'aureus_active_section';

// ---- KYC wizard step persistence (Fix #2) ----
export function readPersistedKycStep(userId: number | string): number {
  try {
    const saved = sessionStorage.getItem(KYC_STEP_KEY(userId));
    const n = saved ? parseInt(saved, 10) : NaN;
    if (n >= 1 && n <= 4) return n;
  } catch { /* sessionStorage unavailable (private mode) */ }
  return 1;
}

export function persistKycStep(userId: number | string, step: number): void {
  try { sessionStorage.setItem(KYC_STEP_KEY(userId), String(step)); } catch { /* non-fatal */ }
}

export function clearPersistedKycStep(userId: number | string): void {
  try { sessionStorage.removeItem(KYC_STEP_KEY(userId)); } catch { /* non-fatal */ }
}

// ---- Dashboard section persistence (Fix #2) ----
export function readPersistedSection(allowed: readonly string[]): string | null {
  try {
    const saved = sessionStorage.getItem(ACTIVE_SECTION_KEY);
    if (saved && allowed.includes(saved)) return saved;
  } catch { /* non-fatal */ }
  return null;
}

export function persistSection(section: string): void {
  try { sessionStorage.setItem(ACTIVE_SECTION_KEY, section); } catch { /* non-fatal */ }
}

// ---- Camera capability + capture (Fix #3) ----
export function isInAppCameraSupported(nav?: Navigator): boolean {
  const n = nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  return !!(n && (n as any).mediaDevices && typeof (n as any).mediaDevices.getUserMedia === 'function');
}

// Detect whether the page is running inside an in-app browser / WebView rather
// than a real mobile browser. These embedded browsers (Google app/GSA, Facebook,
// Instagram, TikTok, LinkedIn, Snapchat, etc.) commonly block getUserMedia and
// even the native file picker, which breaks KYC document/selfie capture. User
// 6214's failure was traced to the Google app's WebView. Detection is a
// best-effort user-agent heuristic used only to improve the guidance message —
// it never changes the fallback behavior itself.
export function isInAppBrowser(nav?: Navigator): boolean {
  const n = nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  const ua = n && typeof (n as any).userAgent === 'string' ? (n as any).userAgent : '';
  if (!ua) return false;
  // Explicit in-app browser signatures.
  const inAppSignatures = [
    'FBAN', 'FBAV', 'FB_IAB',            // Facebook
    'Instagram',                          // Instagram
    'GSA/',                               // Google app (Google Search App)
    'Line/',                              // LINE
    'Twitter', 'TwitterAndroid',          // Twitter/X
    'TikTok', 'BytedanceWebview', 'musical_ly', // TikTok
    'Snapchat',                           // Snapchat
    'LinkedInApp',                        // LinkedIn
    'WhatsApp',                           // WhatsApp
    'Pinterest',                          // Pinterest
  ];
  if (inAppSignatures.some(sig => ua.indexOf(sig) !== -1)) return true;
  // Android System WebView: 'wv' token in a Chrome UA, without a standalone browser.
  if (/;\s?wv\)/.test(ua) || ua.indexOf('; wv') !== -1) return true;
  return false;
}

export interface CameraCaptureDeps {
  // Called with the live MediaStream when the in-app camera opens successfully.
  onStream: (stream: MediaStream) => void;
  // Called to trigger the native <input capture> / photo-picker fallback.
  onFallback: () => void;
  // Called with a user-facing message (or null to clear it).
  onMessage: (message: string | null) => void;
  // Injectable navigator for testing; defaults to the global navigator.
  navigatorRef?: Navigator;
}

// TEMPORARY DIAGNOSTIC (KYC camera field debug) — logs the real reason
// getUserMedia fails on a device WITHOUT changing any behavior (fallback,
// message and stream handling are unchanged). Remove once the failing-device
// error is captured. Prefixed with [KYC-CAM-DIAG] for easy log filtering.
function logCameraDiag(phase: string, extra?: unknown): void {
  try {
    const n: any = typeof navigator !== 'undefined' ? navigator : undefined;
    const w: any = typeof window !== 'undefined' ? window : undefined;
    const err = extra as any;
    console.log('[KYC-CAM-DIAG]', phase, {
      errorName: err && err.name ? err.name : undefined,
      errorMessage: err && err.message ? err.message : undefined,
      isSecureContext: w ? w.isSecureContext : undefined,
      protocol: w && w.location ? w.location.protocol : undefined,
      hasMediaDevices: !!(n && n.mediaDevices),
      hasGetUserMedia: !!(n && n.mediaDevices && typeof n.mediaDevices.getUserMedia === 'function'),
      userAgent: n ? n.userAgent : undefined,
    });
  } catch { /* diagnostics must never throw */ }
}

// Attempt to open the in-app camera for a given document type. On any failure —
// missing API or getUserMedia rejection — trigger the fallback AND emit a message,
// so the button is never silently unresponsive.
export async function attemptCameraCapture(
  type: 'id_document' | 'selfie' | 'address_proof',
  deps: CameraCaptureDeps
): Promise<void> {
  const nav = deps.navigatorRef ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  // Append the in-app-browser guidance when we detect an embedded WebView, since
  // in that case the native picker fallback frequently fails too.
  const inAppHint = isInAppBrowser(nav) ? IN_APP_BROWSER_HINT : '';

  if (!isInAppCameraSupported(nav)) {
    logCameraDiag('unsupported-api'); // TEMPORARY DIAGNOSTIC
    deps.onFallback();
    deps.onMessage(CAMERA_UNAVAILABLE_MESSAGE + inAppHint);
    return;
  }

  try {
    const stream = await nav!.mediaDevices.getUserMedia({
      video: {
        facingMode: type === 'selfie' ? 'user' : 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
    deps.onMessage(null);
    deps.onStream(stream);
  } catch (error) {
    logCameraDiag('getUserMedia-failed', error); // TEMPORARY DIAGNOSTIC
    console.error('Camera access failed:', error);
    deps.onFallback();
    deps.onMessage(CAMERA_BLOCKED_MESSAGE + inAppHint);
  }
}
